import * as React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PublishIcon from '@mui/icons-material/Publish';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { createGiftCard } from '../../api';
import { Notifications, Stories } from '../../store/slices';
import { AppDialog, StatusChip } from '../ui';
import StoryTagsPopup from '../StoryTagsPopup';
import { formatDate } from '../../lib/formatDate';
import { useStyles } from './styles';

type SelectedItem = { storyId: any; authorId: any };

interface StoriesTagTableProps {
    stories: any[];
    paging: { page: number; perPage: number; total: number; [key: string]: any };
    pagingUpdate: (params: any) => void;
    visibleFilters: string[];
    storyTags: any;
}

/** "PRODUCT VISIBILITY" → "Product visibility" for column headers (data keeps the raw name). */
const columnLabel = (name: string) => name.charAt(0) + name.slice(1).toLowerCase();

/**
 * Stories with one column per visible tag category. Clicking the video thumbnail or a tag cell opens
 * the tag assignment popup; the checkboxes drive the bulk publish / gift card actions.
 */
export default function StoriesTagTable({ stories, paging, pagingUpdate, visibleFilters, storyTags }: StoriesTagTableProps) {
    const classes = useStyles();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const dynamicColumns: string[] = (storyTags?.categories || [])
        .filter((cat: any) => visibleFilters?.includes(cat.name))
        .map((cat: any) => cat.name);

    const [pending, setPending] = useState(false);
    const [giftInputValue, setGiftInputValue] = useState<number>(0);
    const [isPopUp, setIsPopUp] = useState(false);
    const [isPublishPopUp, setIsPublishPopUp] = useState(false);
    const [popupStory, setPopupStory] = useState<any | null>(null);
    const [selectedId, setSelectedId] = useState<SelectedItem[]>([]);

    const handleOpenPopup = (story) => setPopupStory(story);
    const handleClosePopup = () => setPopupStory(null);

    const handleCheckBoxChange = (checked: boolean, storyId: any, authorId: any) => {
        if (checked) {
            setSelectedId([...selectedId, { storyId, authorId }]);
        } else {
            setSelectedId(selectedId.filter((item) => item.storyId !== storyId));
        }
    };

    const allOnPageSelected = stories.length > 0 && stories.every((story) => selectedId.some((item) => item.storyId === story.storyId));
    const toggleAll = (checked: boolean) => {
        if (checked) {
            const missing = stories.filter((story) => !selectedId.some((item) => item.storyId === story.storyId)).map((story) => ({ storyId: story.storyId, authorId: story.authorId }));
            setSelectedId([...selectedId, ...missing]);
        } else {
            setSelectedId(selectedId.filter((item) => !stories.some((story) => story.storyId === item.storyId)));
        }
    };

    const handleNavigate = (delta: number) => {
        setPopupStory((prev) => {
            if (!prev) return prev;
            const currentIndex = stories.findIndex((s) => s.storyId === prev.storyId);
            const nextIndex = currentIndex + delta;
            if (nextIndex >= 0 && nextIndex < stories.length) return stories[nextIndex];
            return prev;
        });
    };

    const createGiftCart = async (data: SelectedItem, inputValue: number) => {
        try {
            await createGiftCard({
                data: { gfStoryId: data.storyId, gfAuthorId: data.authorId, amount: inputValue },
                onSuccess: async () => {
                    dispatch(Notifications.setNotifications({ text: 'Gift card created', type: 'success' }));
                },
            });
        } catch (error) {
            dispatch(Notifications.setNotifications({ text: `Something went wrong ID:${data.storyId}`, type: 'error' }));
        }
    };

    const processGiftCards = (items: SelectedItem[], inputValue: number, index = 0) => {
        if (index >= items.length) {
            setGiftInputValue(0);
            setPending(false);
            return;
        }
        createGiftCart(items[index], inputValue);
        setTimeout(() => processGiftCards(items, inputValue, index + 1), 400);
    };

    const publishStory = async (data: SelectedItem) => {
        try {
            dispatch<any>(Stories.updateStory({ id: data.storyId, formData: { isPublished: 1 } })).then(() => {});
        } catch (error) {
            dispatch(Notifications.setNotifications({ text: `Something went wrong ID:${data.storyId}`, type: 'error' }));
        }
    };

    const processPublish = (items: SelectedItem[], index = 0) => {
        if (index >= items.length) {
            setGiftInputValue(0);
            setPending(false);
            return;
        }
        publishStory(items[index]);
        setTimeout(() => processPublish(items, index + 1), 400);
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        pagingUpdate({ ...paging, page: newPage + 1, perPage: paging.perPage });
    };
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        pagingUpdate({ ...paging, page: 1, perPage: +event.target.value });
    };

    return (
        <>
            <AppDialog
                open={isPopUp}
                onClose={() => { setIsPopUp(false); setGiftInputValue(0); }}
                title={`Create gift cards for ${selectedId.length} ${selectedId.length === 1 ? 'story' : 'stories'}`}
                description="One gift card of this amount is issued to the author of every selected story."
                maxWidth="xs"
                actions={(
                    <>
                        <Button variant="outlined" onClick={() => { setIsPopUp(false); setGiftInputValue(0); }}>Cancel</Button>
                        <Button variant="contained" disabled={giftInputValue === 0} onClick={() => { setIsPopUp(false); setPending(true); processGiftCards(selectedId, giftInputValue); }}>
                            Create gift cards
                        </Button>
                    </>
                )}
            >
                <TextField label="Amount" type="number" size="small" fullWidth autoFocus inputProps={{ inputMode: 'numeric', min: 1 }} onChange={(event) => setGiftInputValue(Number(event.target.value))} />
            </AppDialog>

            <AppDialog
                open={isPublishPopUp}
                onClose={() => setIsPublishPopUp(false)}
                title={`Publish ${selectedId.length} ${selectedId.length === 1 ? 'story' : 'stories'}?`}
                description="Every selected story gets a public page. There is no bulk unpublish; use the story page to hide one again."
                maxWidth="xs"
                actions={(
                    <>
                        <Button variant="outlined" onClick={() => setIsPublishPopUp(false)}>Cancel</Button>
                        <Button variant="contained" onClick={() => { setIsPublishPopUp(false); setPending(true); processPublish(selectedId); }}>Publish</Button>
                    </>
                )}
            />

            {selectedId.length > 0 && (
                <div className={classes.toolbar} role="toolbar" aria-label="Bulk actions">
                    <span>{selectedId.length} selected</span>
                    <Button variant="text" size="small" onClick={() => setSelectedId([])}>Clear selection</Button>
                    <div className={classes.toolbarActions}>
                        <Button variant="outlined" size="small" startIcon={<CardGiftcardIcon />} onClick={() => setIsPopUp(true)} disabled={pending}>Gift cards</Button>
                        <Button variant="contained" size="small" startIcon={<PublishIcon />} onClick={() => setIsPublishPopUp(true)} disabled={pending}>Publish</Button>
                    </div>
                </div>
            )}

            <TableContainer className={classes.container}>
                <Table stickyHeader aria-label="Stories and their tags" aria-busy={pending}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    size="small"
                                    checked={allOnPageSelected}
                                    indeterminate={!allOnPageSelected && selectedId.length > 0}
                                    onChange={(event) => toggleAll(event.target.checked)}
                                    inputProps={{ 'aria-label': 'Select all stories on this page' }}
                                />
                            </TableCell>
                            <TableCell>Story</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Video</TableCell>
                            {dynamicColumns.map((col) => <TableCell key={col}>{columnLabel(col)}</TableCell>)}
                            <TableCell>Published</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {stories.map((story) => (
                            <TableRow key={story.storyId} hover selected={selectedId.some((item) => item.storyId === story.storyId)}>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        size="small"
                                        checked={selectedId.some((item) => item.storyId === story.storyId)}
                                        onChange={(event) => handleCheckBoxChange(event.target.checked, story.storyId, story.authorId)}
                                        inputProps={{ 'aria-label': `Select story ${story.storyId}` }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Link to={`/stories/${story.storyId}/details`} className={classes.mono} style={{ fontWeight: 600, color: 'inherit', textDecoration: 'none' }}>
                                        #{story.storyId}
                                    </Link>
                                </TableCell>
                                <TableCell className={classes.nowrap}>{formatDate(story.date)}</TableCell>
                                <TableCell>
                                    <button type="button" className={classes.thumbButton} onClick={() => handleOpenPopup(story)} aria-label={`Set tags for story ${story.storyId}`}>
                                        {story.imageLink ? <img src={story.imageLink} alt="" loading="lazy" /> : null}
                                    </button>
                                </TableCell>
                                {dynamicColumns.map((col) => {
                                    const names = (story.tags || []).filter((tag) => tag.category === col).map((tag) => tag.tagName);
                                    return (
                                        <TableCell key={col} className={classes.tagCell} onClick={() => handleOpenPopup(story)} title="Click to set tags">
                                            {names.length > 0
                                                ? <span className={classes.tagText}>{names.join(', ')}</span>
                                                : <span className={classes.tagEmpty}>—</span>}
                                        </TableCell>
                                    );
                                })}
                                <TableCell>
                                    <StatusChip size="sm" dot tone={story.isPublished ? 'success' : 'neutral'}>{story.isPublished ? 'Yes' : 'No'}</StatusChip>
                                </TableCell>
                                <TableCell>
                                    <div className={classes.actions}>
                                        <Button variant="outlined" size="small" startIcon={<EditOutlinedIcon />} onClick={() => navigate(`/stories/${story.storyId}/details`)}>
                                            Edit
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={paging.total}
                rowsPerPage={paging.perPage}
                page={Math.max(0, paging.page - 1)}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <StoryTagsPopup
                key={popupStory?.storyId}
                open={!!popupStory}
                onClose={handleClosePopup}
                story={popupStory}
                STORY_TAGS={storyTags?.tags || []}
                STORY_CATEGORIES={storyTags?.categories || []}
                onPrev={() => handleNavigate(-1)}
                onNext={() => handleNavigate(1)}
            />
        </>
    );
}
